// ---------------------------------------------------------------------------
// Layout persistence — load/save panel sizes to localStorage
// ---------------------------------------------------------------------------

import {
  STORAGE_KEYS,
  DEFAULT_EXPLORER_WIDTH,
  DEFAULT_EDITOR_WIDTH,
  DEFAULT_RIGHT_PANEL_WIDTH,
  DEFAULT_BOTTOM_PANEL_HEIGHT,
  DEFAULT_SECONDARY_SIDEBAR_WIDTH,
  LAYOUT_SAVE_DEBOUNCE_MS,
  EXPLORER_MIN_WIDTH,
  EXPLORER_MAX_WIDTH,
  RIGHT_PANEL_MIN_WIDTH,
  RIGHT_PANEL_MAX_WIDTH,
  BOTTOM_PANEL_MIN_HEIGHT,
  BOTTOM_PANEL_MAX_HEIGHT,
  SECONDARY_SIDEBAR_MIN_WIDTH,
  SECONDARY_SIDEBAR_MAX_WIDTH,
  MIN_EDITOR_WIDTH,
  MAX_EDITOR_WIDTH,
} from './constants.js';
import type { LayoutState, LayoutStateV2, RightPanelTab, SecondarySidebarTab } from './constants.js';

const DEFAULTS: LayoutState = {
  explorerWidth: DEFAULT_EXPLORER_WIDTH,
  explorerCollapsed: false,
  editorSplitPct: DEFAULT_EDITOR_WIDTH,
  rightPanelWidth: DEFAULT_RIGHT_PANEL_WIDTH,
  rightPanelOpen: true,
  rightPanelTab: 'chatbot' as RightPanelTab,
  bottomPanelHeight: DEFAULT_BOTTOM_PANEL_HEIGHT,
  showSearch: false,
  showDiagnostics: false,
};

/** Clamp a number between min and max */
function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/** Load layout from localStorage, merging with defaults and clamping values */
export function loadLayout(): LayoutState {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.LAYOUT);
    if (!raw) return { ...DEFAULTS };
    const stored = JSON.parse(raw) as Partial<LayoutState>;
    const merged = { ...DEFAULTS, ...stored };

    // Clamp numeric values to valid ranges
    merged.explorerWidth = clamp(merged.explorerWidth, EXPLORER_MIN_WIDTH, EXPLORER_MAX_WIDTH);
    merged.editorSplitPct = clamp(merged.editorSplitPct, MIN_EDITOR_WIDTH, MAX_EDITOR_WIDTH);
    merged.rightPanelWidth = clamp(merged.rightPanelWidth, RIGHT_PANEL_MIN_WIDTH, RIGHT_PANEL_MAX_WIDTH);
    merged.bottomPanelHeight = clamp(merged.bottomPanelHeight, BOTTOM_PANEL_MIN_HEIGHT, BOTTOM_PANEL_MAX_HEIGHT);

    // Bottom panels should not auto-restore as open — user opens them when needed
    merged.showSearch = false;
    merged.showDiagnostics = false;

    return merged;
  } catch {
    return { ...DEFAULTS };
  }
}

/** Save layout to localStorage */
export function saveLayout(state: LayoutState): void {
  try {
    localStorage.setItem(STORAGE_KEYS.LAYOUT, JSON.stringify(state));
  } catch {
    // localStorage full or disabled — silently ignore
  }
}

let _debounceTimer: ReturnType<typeof setTimeout> | null = null;

/** Debounced save to avoid thrashing during drag operations */
export function saveLayoutDebounced(state: LayoutState): void {
  if (_debounceTimer !== null) clearTimeout(_debounceTimer);
  _debounceTimer = setTimeout(() => {
    saveLayout(state);
    _debounceTimer = null;
  }, LAYOUT_SAVE_DEBOUNCE_MS);
}

// ---------------------------------------------------------------------------
// V2 Layout persistence — zone model
// ---------------------------------------------------------------------------

const DEFAULTS_V2: LayoutStateV2 = {
  version: 2,
  primarySidebarWidth: DEFAULT_EXPLORER_WIDTH,
  primarySidebarCollapsed: false,
  primarySidebarActiveItem: 'explorer',
  editorSplitPct: DEFAULT_EDITOR_WIDTH,
  bottomPanelHeight: DEFAULT_BOTTOM_PANEL_HEIGHT,
  bottomPanelCollapsed: true,
  bottomPanelActiveTab: 'diagnostics',
  secondarySidebarWidth: DEFAULT_SECONDARY_SIDEBAR_WIDTH,
  secondarySidebarCollapsed: false,
  secondarySidebarActiveTab: 'chatbot',
  diagramView: 'diagram',
  focusMode: 'none',
};

/** Migrate from v1 LayoutState to v2 LayoutStateV2 */
function migrateV1toV2(v1: Partial<LayoutState>): LayoutStateV2 {
  // Map right panel tab → secondary sidebar tab (solver/history moved to bottom panel)
  const tabMap: Record<string, SecondarySidebarTab> = {
    chatbot: 'chatbot', health: 'health', verification: 'verification',
    requirements: 'requirements', ingest: 'ingest', builder: 'builder',
    quickRef: 'quickRef',
  };
  const mappedTab = tabMap[v1.rightPanelTab ?? ''] ?? 'chatbot';

  return {
    ...DEFAULTS_V2,
    primarySidebarWidth: v1.explorerWidth ?? DEFAULTS_V2.primarySidebarWidth,
    primarySidebarCollapsed: v1.explorerCollapsed ?? DEFAULTS_V2.primarySidebarCollapsed,
    editorSplitPct: v1.editorSplitPct ?? DEFAULTS_V2.editorSplitPct,
    secondarySidebarWidth: v1.rightPanelWidth ?? DEFAULTS_V2.secondarySidebarWidth,
    secondarySidebarCollapsed: !(v1.rightPanelOpen ?? true),
    secondarySidebarActiveTab: mappedTab,
    bottomPanelCollapsed: !(v1.showSearch || v1.showDiagnostics),
    bottomPanelActiveTab: v1.showDiagnostics ? 'diagnostics' : v1.showSearch ? 'search' : 'diagnostics',
    bottomPanelHeight: v1.bottomPanelHeight ?? DEFAULTS_V2.bottomPanelHeight,
  };
}

/** Load v2 layout from localStorage, migrating from v1 if needed */
export function loadLayoutV2(): LayoutStateV2 {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.LAYOUT);
    if (!raw) return { ...DEFAULTS_V2 };

    const stored = JSON.parse(raw);

    if (stored.version === 2) {
      const merged = { ...DEFAULTS_V2, ...stored } as LayoutStateV2;
      // Clamp numeric values
      merged.primarySidebarWidth = clamp(merged.primarySidebarWidth, EXPLORER_MIN_WIDTH, EXPLORER_MAX_WIDTH);
      merged.editorSplitPct = clamp(merged.editorSplitPct, MIN_EDITOR_WIDTH, MAX_EDITOR_WIDTH);
      merged.secondarySidebarWidth = clamp(merged.secondarySidebarWidth, SECONDARY_SIDEBAR_MIN_WIDTH, SECONDARY_SIDEBAR_MAX_WIDTH);
      merged.bottomPanelHeight = clamp(merged.bottomPanelHeight, BOTTOM_PANEL_MIN_HEIGHT, BOTTOM_PANEL_MAX_HEIGHT);
      // Bottom panel always starts collapsed
      merged.bottomPanelCollapsed = true;
      return merged;
    }

    // Migrate from v1
    return migrateV1toV2(stored as Partial<LayoutState>);
  } catch {
    return { ...DEFAULTS_V2 };
  }
}

/** Save v2 layout to localStorage */
export function saveLayoutV2(state: LayoutStateV2): void {
  try {
    localStorage.setItem(STORAGE_KEYS.LAYOUT, JSON.stringify(state));
  } catch {
    // localStorage full or disabled — silently ignore
  }
}

let _debounceTimerV2: ReturnType<typeof setTimeout> | null = null;

/** Debounced save for v2 layout */
export function saveLayoutDebouncedV2(state: LayoutStateV2): void {
  if (_debounceTimerV2 !== null) clearTimeout(_debounceTimerV2);
  _debounceTimerV2 = setTimeout(() => {
    saveLayoutV2(state);
    _debounceTimerV2 = null;
  }, LAYOUT_SAVE_DEBOUNCE_MS);
}
